//External libs
import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from 'src/app/common/service';
import UOMMasterModel from './uom.model';

/**
 * @service UomService
 * @description Service for managing Unit of Measure (UOM) definitions within the Material Management System (MMS).
 * Units of Measure are fundamental to inventory control, defining how items are quantified, purchased, stored, and consumed.
 *
 * Examples of UOMs include:
 * - **Mass**: Kilogram (kg), Gram (g), Tonne (t).
 * - **Volume**: Liter (L), Milliliter (ml).
 * - **Count**: Piece (pc), Bag, Box, Dozen.
 * - **Length**: Meter (m), Centimeter (cm).
 *
 * This service provides the interface to retrieve the master list of UOMs from the backend.
 * It is essential for:
 * 1. **Item Definition**: Assigning base units to items in the Item Master.
 * 2. **Transaction Recording**: Specifying quantities in Purchase Orders, Goods Receipts, and Issues.
 * 3. **Conversions**: Supporting logic (handled elsewhere or via UOM Conversion service) to translate between purchasing units (e.g., Bags) and consumption units (e.g., kg).
 *
 * The service interacts with the `/mms/uommaster/` backend endpoint.
 *
 * @feature 1. Retrieve all defined Units of Measure.
 * @feature 2. Support for large datasets (pageSize=2000) to ensure all units are loaded.
 * @feature 3. Robust management of concurrent HTTP requests to prevent memory leaks.
 *
 * @architecture
 * Extends base Service<UOMMasterModel> providing REST API integration.
 * Uses HttpClient for HTTP operations and manages subscriptions for cleanup.
 *
 * @service-endpoint /mms/uommaster/
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class UomService extends Service<UOMMasterModel> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions.
   * Used to maintain references to active Observables so they can be unsubscribed
   * when the service is destroyed or when cancellation is requested.
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient.
   *
   * Initializes the service by calling the parent Service constructor with the
   * HttpClient instance and the specific API endpoint path (`/mms/uommaster/`).
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests to the backend.
   * @returns {void}
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/mms/uommaster/');
  }

  /**
   * @method getAllUom
   * @async
   * @description Fetch all Unit of Measure records from the backend.
   *
   * This method performs a GET request to the service endpoint. It is configured to retrieve
   * a large number of records (pageSize=2000) to ensure all UOM definitions are loaded,
   * ordered by creation date ('new').
   *
   * @returns {Promise<Array<UOMMasterModel>>} Promise resolving to an array of UOMMasterModel objects.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP GET request.
   * - Pushes the subscription to `ongoingRequests`.
   *
   * @example
   * uomService.getAllUom().then(uoms => {
   *   console.log('Available UOMs:', uoms);
   * });
   */
  public getAllUom() {
    return new Promise<Array<UOMMasterModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<UOMMasterModel>>(`${this.baseUrl}${this.serviceUrl}?pageSize=2000&order=new`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  //To Cancel the onGoing Http Requests
  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks.
   *
   * This method iterates through the `ongoingRequests` array and calls `unsubscribe()`
   * on each subscription. This is typically called when a component using this service
   * is destroyed (ngOnDestroy) to ensure that no network requests continue in the background.
   *
   * @returns {void}
   * @side-effect
   * - Aborts all pending HTTP requests tracked in `ongoingRequests`.
   * - Resets `ongoingRequests` to an empty array.
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
